import React, { useMemo, useState } from "react";
import YearCalendarBoard from "./YearCalendarBoard";

export default function ContractHoursCalendarEditor({
  entries,
  machines,
  calendarMaps,
  onAdd,
  onUpdate,
  onRemove,
  onSetDayHours,
  onFillYear,
}) {
  const [year, setYear] = useState(2026);
  const [openMachineId, setOpenMachineId] = useState(null);
  const [hourValue, setHourValue] = useState(24);

  const dateMeta = useMemo(() => {
    const out = {};
    if (!openMachineId) return out;
    const map = calendarMaps?.contractHoursByMachineDate || {};
    Object.entries(map).forEach(([key, hours]) => {
      const [machineId, date] = key.split("::");
      if (machineId !== openMachineId || !date?.startsWith(`${year}-`)) return;
      out[date] = {
        tone: Number(hours) > 0 ? "ok" : "muted",
        label: `${hours}h`,
        badge: `${Math.round(Number(hours) || 0)}`,
      };
    });
    return out;
  }, [calendarMaps, openMachineId, year]);

  const visibleEntries = useMemo(
    () => (entries || []).filter((entry) => entry.machineId === openMachineId),
    [entries, openMachineId]
  );

  return (
    <div className="swb-base-section">
      <section className="swb-panel">
        <div className="swb-panel__head">
          <h3>Calendário de horas de contrato (por máquina)</h3>
          <span className="swb-subtle">Todas as máquinas (cabeçalho por prefixo)</span>
        </div>
        <p className="swb-subtle">
          Clique no cabeçalho da máquina e depois nos dias para aplicar horas diferentes por dia.
        </p>
      </section>

      <div className="swb-accordion-list">
        {(machines || []).map((machine) => {
          const isOpen = openMachineId === machine.id;
          const count = (entries || []).filter((entry) => entry.machineId === machine.id).length;

          return (
            <section key={machine.id} className={`swb-accordion ${isOpen ? "is-open" : ""}`}>
              <button
                type="button"
                className="swb-accordion__head"
                onClick={() => setOpenMachineId((prev) => (prev === machine.id ? null : machine.id))}
              >
                <span>{machine.prefix} ({machine.code})</span>
                <small>{count} registro(s)</small>
              </button>

              {isOpen ? (
                <div className="swb-accordion__body">
                  <div className="swb-inline-actions" style={{ marginBottom: 8 }}>
                    <label className="swb-inline-field">
                      <span>Horas para aplicar</span>
                      <input
                        type="number"
                        className="swb-mini-input"
                        min="0"
                        step="0.5"
                        value={hourValue}
                        onChange={(e) => setHourValue(Number(e.target.value) || 0)}
                      />
                    </label>
                    <button
                      type="button"
                      className="swb-btn"
                      onClick={() => onFillYear?.(machine.id, year, hourValue)}
                    >
                      Preencher ano ({hourValue}h)
                    </button>
                    <button type="button" className="swb-btn" onClick={() => onAdd(machine.id)}>
                      Adicionar bloco manual
                    </button>
                  </div>

                  <YearCalendarBoard
                    year={year}
                    onYearChange={setYear}
                    dateMeta={dateMeta}
                    onDayClick={(date) => onSetDayHours?.(machine.id, date, hourValue)}
                    subtitle="Clique no dia para definir horas de contrato."
                  />

                  <section className="swb-panel">
                    <div className="swb-panel__head">
                      <h3>Registros</h3>
                      <span className="swb-subtle">{visibleEntries.length} item(ns)</span>
                    </div>
                    <div className="swb-table-wrap">
                      <table className="swb-table swb-table--compact">
                        <thead>
                          <tr>
                            <th>Início</th>
                            <th>Fim</th>
                            <th>Horas/dia</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleEntries.length ? (
                            visibleEntries.slice(0, 40).map((entry) => (
                              <tr key={entry.id}>
                                <td>
                                  <input
                                    className="swb-mini-input"
                                    type="date"
                                    value={entry.startDate || ""}
                                    onChange={(e) => onUpdate(entry.id, "startDate", e.target.value)}
                                  />
                                </td>
                                <td>
                                  <input
                                    className="swb-mini-input"
                                    type="date"
                                    value={entry.endDate || ""}
                                    onChange={(e) => onUpdate(entry.id, "endDate", e.target.value)}
                                  />
                                </td>
                                <td>
                                  <input
                                    className="swb-mini-input"
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={entry.hoursPerDay ?? 0}
                                    onChange={(e) => onUpdate(entry.id, "hoursPerDay", e.target.value)}
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="swb-btn swb-btn--danger"
                                    onClick={() => onRemove(entry.id)}
                                  >
                                    Excluir
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className="swb-empty-cell">
                                Sem registros para esta máquina.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}
